import type { CampaignGroupSearchRequest } from 'omega-rtc/types/ts/journey-campaign-group-search';
import type { SheetSyncCampaignRef } from 'omega-rtc/types/ts/journey-sheet-sync';
import {
  API_REQUEST,
  SERVICE_NAME,
  CHECK_NETWORK,
  REQUEST,
  SUCCESS,
  FAILURE,
} from 'omega-rtc/middlewares/api-middleware';
import { TridentCampaignForm } from 'omega-rtc/types/ucd/campaign';
import type { CreateUCDProject } from 'omega-rtc/types/ucd/project';
import type { FSA } from 'types/redux';

import {
  SERVICE_NAMES,
  SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
  SMART_CAMPAIGN_MYSTIQUE_CRM,
  SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA,
  SMART_CAMPAIGN_MYSTIQUE_GRAB_GXS,
  SMART_CAMPAIGN_CHANNELS,
} from 'omega-rtc/constants/ucd';
import {
  UCD_CAMPAIGN_GROUP_SEARCH_PATH,
  UCD_SHEET_SYNC_APPLY_PATH,
  UCD_SHEET_SYNC_PREVIEW_PATH,
  MYSTIQUE_ATTRIBUTES_PATH,
  MYSTIQUE_COUNTRIES_CONFIG_PATH,
  MYSTIQUE_GENERATE_CONTENT_V1_PATH,
  MYSTIQUE_GENERATE_CONTENT_V2_PATH,
  MYSTIQUE_PRODUCT_OFFERINGS_PATH,
  UCD_CONTENT_GENERATION_CONFIG_LATEST_PATH,
  UCD_CONTENT_GENERATION_TEST_GENERATE_PATH,
  UCD_AUDIENCES_INFO_PATH,
  UCD_AUDIENCES_LIST_PATH,
  UCD_AUDIENCES_PATH,
  UCD_EVENTS_LIST_PATH,
  UCD_EVENTS_PATH,
} from 'omega-rtc/config';
import { ContentConfigStatus } from 'omega-rtc/types/ts/content-config';
import type { AudienceBodyApi, EventBodyApi } from 'omega-rtc/types/ts/content-config';
import type {
  CreateJourneyCampaignGroupRequest,
  JourneyCampaignGroupCampaign,
  JourneyCampaignGroupResponse,
  UpdateJourneyCampaignGroupRequest,
} from 'omega-rtc/types/ts/journey-campaign-group';
import { isContentGenerationEndpointMigrationEnabled } from 'omega-rtc/utils/portal-config';

export const SEARCH_UCD_CAMPAIGNS = 'SEARCH_UCD_CAMPAIGNS';

export const RESET_NEWLY_CREATED_CAMPAIGN = 'RESET_NEWLY_CREATED_CAMPAIGN';

export const resetNewlyCreatedCampaign = () => ({
  type: RESET_NEWLY_CREATED_CAMPAIGN,
});

export function searchUCDCampaigns(searchParams: any): FSA {
  return {
    type: SEARCH_UCD_CAMPAIGNS,
    payload: {
      method: 'POST',
      url: '/ucd/campaigns/search',
      data: searchParams,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const SEARCH_UCD_CAMPAIGN_GROUPS = 'SEARCH_UCD_CAMPAIGN_GROUPS';

export function searchUCDCampaignGroups(data: CampaignGroupSearchRequest): FSA {
  return {
    type: SEARCH_UCD_CAMPAIGN_GROUPS,
    payload: { method: 'POST', url: UCD_CAMPAIGN_GROUP_SEARCH_PATH, data },
    meta: {
      [API_REQUEST]: true,
      searchKey: JSON.stringify(data),
    },
  };
}

export const SEARCH_UCD_PROJECTS = 'SEARCH_UCD_PROJECTS';

export function searchUCDProjects(searchParams: any): FSA {
  return {
    type: SEARCH_UCD_PROJECTS,
    payload: {
      method: 'POST',
      url: '/ucd/projects/search',
      data: searchParams,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const SUGGEST_UCD_PROJECTS = 'SUGGEST_UCD_PROJECTS';

export function suggestUCDProjects(searchParams: any): FSA {
  return {
    type: SUGGEST_UCD_PROJECTS,
    payload: {
      method: 'POST',
      url: '/ucd/projects/search',
      data: searchParams,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_PROJECT = 'GET_UCD_PROJECT';

export function getUCDProject(projectID: string): FSA {
  return {
    type: GET_UCD_PROJECT,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_PROJECT_METRICS = 'GET_UCD_PROJECT_METRICS';

export function getUCDProjectMetrics(projectID: string): FSA {
  return {
    type: GET_UCD_PROJECT_METRICS,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/metrics`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_CAMPAIGN_METRICS = 'GET_UCD_CAMPAIGN_METRICS';

export function getUCDCampaignMetrics(
  projectID: string,
  campaignID: string,
  params: Object,
): FSA {
  return {
    type: GET_UCD_CAMPAIGN_METRICS,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/metrics`,
      params,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_JOURNEY_BUSINESS_METRICS =
  'GET_UCD_JOURNEY_BUSINESS_METRICS';

export function getJourneyCampaignBusinessMetrics(
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: GET_UCD_JOURNEY_BUSINESS_METRICS,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/business`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_JOURNEY_PROGRESS = 'GET_JOURNEY_PROGRESS';

export function getJourneyCampaignProgress(
  countryID: string,
  campaignID: string,
): FSA {
  return {
    type: GET_JOURNEY_PROGRESS,
    payload: {
      method: 'GET',
      url: `/campaigns/${countryID}/${campaignID}/progress`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_CAMPAIGN_METRICS_BY_DATE =
  'GET_UCD_CAMPAIGN_METRICS_BY_DATE';

export function getUCDCampaignMetricsByDate(
  projectID: string,
  campaignID: string,
  params: Object,
): FSA {
  return {
    type: GET_UCD_CAMPAIGN_METRICS_BY_DATE,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/metrics`,
      params,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_CAMPAIGN_METRICS_BENCHMARK =
  'GET_UCD_CAMPAIGN_BENCHMARK_METRICS';

export function getUCDCampaignMetricsBenchmark(
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: GET_UCD_CAMPAIGN_METRICS_BENCHMARK,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/metrics/benchmark?types=average,percentile90`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const CREATE_UCD_PROJECT = 'CREATE_UCD_PROJECT';

export function createUCDProject(data: CreateUCDProject): FSA {
  return {
    type: CREATE_UCD_PROJECT,
    payload: {
      method: 'POST',
      url: '/ucd/projects',
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const UPDATE_UCD_PROJECT = 'UPDATE_UCD_PROJECT';

export function updateUCDProject(
  projectID: string,
  data: CreateUCDProject,
): FSA {
  return {
    type: UPDATE_UCD_PROJECT,
    payload: {
      method: 'PUT',
      url: `/ucd/projects/${projectID}`,
      data: {
        project: data,
        projectID,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const CREATE_UCD_CAMPAIGN = 'CREATE_UCD_CAMPAIGN';

export function createUCDCampaign(
  data: CreateUCDCampaign,
  projectID: string,
): FSA {
  return {
    type: CREATE_UCD_CAMPAIGN,
    payload: {
      method: 'POST',
      url: `/ucd/projects/${projectID}/campaigns`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const CREATE_JOURNEY_CAMPAIGN_GROUP = 'CREATE_JOURNEY_CAMPAIGN_GROUP';
export const GET_JOURNEY_CAMPAIGN_GROUP = 'GET_JOURNEY_CAMPAIGN_GROUP';
export const RESET_JOURNEY_CAMPAIGN_GROUP = 'RESET_JOURNEY_CAMPAIGN_GROUP';
export const UPDATE_JOURNEY_CAMPAIGN_GROUP = 'UPDATE_JOURNEY_CAMPAIGN_GROUP';

export function createJourneyCampaignGroup(
  data: CreateJourneyCampaignGroupRequest,
): FSA {
  return {
    type: CREATE_JOURNEY_CAMPAIGN_GROUP,
    payload: {
      method: 'POST',
      url: '/ucd/campaign-groups',
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export function getJourneyCampaignGroup(campaignGroupID: string): FSA {
  return {
    type: GET_JOURNEY_CAMPAIGN_GROUP,
    payload: {
      method: 'GET',
      url: `/ucd/campaign-groups/${campaignGroupID}`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS = 'GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS';

// Older group responses omit the approval flag. Read it from the same endpoint
// as the detail page without overwriting the single-campaign detail state.
// Keep lookups independent so a failed country cannot discard successful siblings.
export function getJourneyCampaignGroupApprovalStatus(
  campaignGroup: JourneyCampaignGroupResponse,
  campaign: JourneyCampaignGroupCampaign,
): FSA {
  return {
    type: GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS,
    payload: getUCDCampaign(campaign.projectID, campaign.campaignID).payload,
    meta: { [API_REQUEST]: true, campaignGroup, campaign },
  };
}

export function updateJourneyCampaignGroup(
  campaignGroupID: string,
  data: Omit<UpdateJourneyCampaignGroupRequest, 'campaignGroupID'>,
): FSA {
  return {
    type: UPDATE_JOURNEY_CAMPAIGN_GROUP,
    payload: {
      method: 'PUT',
      url: `/ucd/campaign-groups/${campaignGroupID}`,
      data: {
        ...data,
        campaignGroupID,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export function resetJourneyCampaignGroup(): FSA {
  return { type: RESET_JOURNEY_CAMPAIGN_GROUP };
}

export const CREATE_TRIDENT_CAMPAIGN = 'CREATE_TRIDENT_CAMPAIGN';

export function createTridentCampaign(
  data: TridentCampaignForm,
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: CREATE_TRIDENT_CAMPAIGN,
    payload: {
      method: 'POST',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/trident`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const CREATE_JOURNEY_CAMPAIGN = 'CREATE_JOURNEY_CAMPAIGN';

export function createJourneyCampaign(
  data: Object, // TODO: define the DTO for journey campaign
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: CREATE_JOURNEY_CAMPAIGN,
    payload: {
      method: 'POST',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/trident`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const UPDATE_JOURNEY_CAMPAIGN = 'UPDATE_JOURNEY_CAMPAIGN';

export function updateJourneyCampaign(
  data: Object, // TODO: define the DTO for journey campaign
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: UPDATE_JOURNEY_CAMPAIGN,
    payload: {
      method: 'PUT',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/trident`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const UPDATE_TRIDENT_CAMPAIGN = 'UPDATE_TRIDENT_CAMPAIGN';

export function updateTridentCampaign(
  data: TridentCampaignForm,
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: UPDATE_TRIDENT_CAMPAIGN,
    payload: {
      method: 'PUT',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/trident`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DEACTIVATE_TRIDENT_CAMPAIGN = 'DEACTIVATE_TRIDENT_CAMPAIGN';

export function deactivateTridentCampaign(
  countryID: string,
  campaignID: string,
  user: string,
): FSA {
  return {
    type: DEACTIVATE_TRIDENT_CAMPAIGN,
    payload: {
      method: 'PATCH',
      url: `/campaigns/${countryID}/${campaignID}`,
      data: { status: 2, adminEmail: user },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_TRIDENT_CAMPAIGN = 'GET_TRIDENT_CAMPAIGN';

export function getTridentCampaign(projectID: string, campaignID: string): FSA {
  return {
    type: GET_TRIDENT_CAMPAIGN,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/trident`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const UPDATE_UCD_CAMPAIGN = 'UPDATE_UCD_CAMPAIGN';

export type UpdateJourneyCampaignRequest = {
  campaign: {
    [key: string]: any;
    budgetSource: string;
    CVP: string;
    end: string;
    journeyMetadata: {
      [key: string]: any;
      source: {
        [key: string]: any;
        type: 'GOOGLE_SHEET';
        url: string;
      };
    };
    name: string;
    start: string;
    verticals: string[];
  };
  user: string;
};

export function updateUCDCampaign(
  data: CreateUCDCampaign | UpdateJourneyCampaignRequest,
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: UPDATE_UCD_CAMPAIGN,
    payload: {
      method: 'PUT',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}`,
      data,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_CONFIGS = 'GET_UCD_CONFIG';

export function getUCDConfigs(): FSA {
  return {
    type: GET_UCD_CONFIGS,
    payload: {
      method: 'GET',
      url: '/ucd/configs',
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_UCD_CAMPAIGN = 'GET_UCD_CAMPAIGN';

export function getUCDCampaign(projectID: string, campaignID: string): FSA {
  return {
    type: GET_UCD_CAMPAIGN,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_JOURNEY_CAMPAIGN_COMMS_TIMELINE =
  'GET_JOURNEY_CAMPAIGN_COMMS_TIMELINE';

export function getJourneyCampaignCommsTimeline(
  projectID: string,
  campaignID: string,
  params: { startDate: string; endDate: string },
): FSA {
  return {
    type: GET_JOURNEY_CAMPAIGN_COMMS_TIMELINE,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/journey/comms-timeline`,
      params,
    },
    meta: { [API_REQUEST]: true },
  };
}

export const GET_JOURNEY_CAMPAIGN_USER_AUDIT = 'GET_JOURNEY_CAMPAIGN_USER_AUDIT';
export const CLEAR_JOURNEY_CAMPAIGN_USER_AUDIT = 'CLEAR_JOURNEY_CAMPAIGN_USER_AUDIT';

export const clearJourneyCampaignUserAudit = () => ({
  type: CLEAR_JOURNEY_CAMPAIGN_USER_AUDIT,
});

export function getJourneyCampaignUserAudit(
  projectID: string,
  campaignID: string,
  params: { userID: string; startDate: string; endDate: string },
): FSA {
  return {
    type: GET_JOURNEY_CAMPAIGN_USER_AUDIT,
    payload: {
      method: 'GET',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}/journey/user-audit`,
      params,
    },
    meta: { [API_REQUEST]: true },
  };
}

export const PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC = 'PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC';
export const APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC = 'APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC';

// Auth state can fall back to a display name when an email claim is missing.
// Keep sync available, but leave the requester unknown instead of sending that name.
const sheetSyncRequesterEmail = (user: string | undefined): string => {
  const email = typeof user === 'string' ? user.trim() : '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
};

// UCD preview identifies campaigns by campaignID/projectID and resolves countryID from storage.
// The returned countryID must match the reviewed selection before Apply.
export function previewJourneyCampaignSheetSync(
  campaignGroupID: string,
  campaign: SheetSyncCampaignRef | SheetSyncCampaignRef[],
  user: string | undefined,
): FSA {
  return {
    type: PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC,
    payload: {
      method: 'POST',
      url: UCD_SHEET_SYNC_PREVIEW_PATH(campaignGroupID),
      data: {
        user: sheetSyncRequesterEmail(user),
        campaigns: (Array.isArray(campaign) ? campaign : [campaign]).map(
          ({ campaignID, projectID }) => ({ campaignID, projectID }),
        ),
      },
    },
    meta: { [API_REQUEST]: true },
  };
}

export function applyJourneyCampaignSheetSync(
  campaignGroupID: string,
  previewToken: string,
  campaign: SheetSyncCampaignRef | SheetSyncCampaignRef[],
  user: string | undefined,
): FSA {
  return {
    type: APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC,
    payload: {
      method: 'POST',
      url: UCD_SHEET_SYNC_APPLY_PATH(campaignGroupID),
      data: {
        user: sheetSyncRequesterEmail(user),
        previewToken,
        campaigns: (Array.isArray(campaign) ? campaign : [campaign]).map(
          ({ campaignID, countryID, projectID }) => {
            const numericCountryID = Number(countryID);
            return {
              campaignID,
              countryID: typeof countryID === 'string' && countryID.trim() !== ''
                && Number.isSafeInteger(numericCountryID) ? numericCountryID : countryID,
              projectID,
            };
          },
        ),
      },
    },
    meta: { [API_REQUEST]: true },
  };
}

export const RESET_UCD_CAMPAIGN = 'RESET_UCD_CAMPAIGN';

export const resetUCDCampaign = () => ({
  type: RESET_UCD_CAMPAIGN,
});

export const UPDATE_UCD_CAMPAIGN_STATUS = 'UPDATE_UCD_CAMPAIGN_STATUS';

export function updateUCDCampaignStatus(
  status: string,
  user: string,
  projectID: string,
  campaignID: string,
): FSA {
  return {
    type: UPDATE_UCD_CAMPAIGN_STATUS,
    payload: {
      method: 'PATCH',
      url: `/ucd/projects/${projectID}/campaigns/${campaignID}`,
      data: {
        status,
        user,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export function updateUCDCampaignStatuses(
  status: string,
  user: string,
  campaigns: JourneyCampaignGroupCampaign[],
): FSA {
  return {
    type: UPDATE_UCD_CAMPAIGN_STATUS,
    payload: campaigns.map((campaign) => ({
      method: 'PATCH',
      url: `/ucd/projects/${campaign.projectID}/campaigns/${campaign.campaignID}`,
      data: {
        status,
        user,
      },
    })),
    meta: {
      [API_REQUEST]: true,
      batch: true,
    },
  };
}

export const UPDATE_UCD_PROJECT_STATUS = 'UPDATE_UCD_PROJECT_STATUS';

export function updateUCDProjectStatus(
  status: string,
  user: string,
  projectID: string,
): FSA {
  return {
    type: UPDATE_UCD_PROJECT_STATUS,
    payload: {
      method: 'PATCH',
      url: `/ucd/projects/${projectID}`,
      data: {
        status,
        user,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const UCD_GENERATE_MYSTIQUE_CONTENT = 'UCD_GENERATE_MYSTIQUE_CONTENT';
export const CONTENT_GENERATION_GET_COUNTRIES_CONFIG = 'CONTENT_GENERATION_GET_COUNTRIES_CONFIG';
export const CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS = 'CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS';
export const CONTENT_GENERATION_LIST_ATTRIBUTES = 'CONTENT_GENERATION_LIST_ATTRIBUTES';
export const CONTENT_GENERATION_GENERATE_MYSTIQUE_V1 = 'CONTENT_GENERATION_GENERATE_MYSTIQUE_V1';
export const CONTENT_CONFIG_GENERATE_MYSTIQUE_V2 = 'CONTENT_CONFIG_GENERATE_MYSTIQUE_V2';
export const CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE = 'CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE';
export const CONTENT_CONFIG_GENERATION_CONCURRENCY = 5;

const mystiqueContentGenerationMeta = {
  [API_REQUEST]: true,
  [SERVICE_NAME]: SERVICE_NAMES.MYSTIQUE,
  [CHECK_NETWORK]: true,
};

const ucdContentGenerationMeta = {
  [API_REQUEST]: true,
  [CHECK_NETWORK]: true,
};

const shouldUseUcdContentGenerationPayload = (ucdPayload?: Record<string, any>) =>
  isContentGenerationEndpointMigrationEnabled() && Boolean(ucdPayload);

const ucdContentGenerationResponseMeta = (ucdPayload?: Record<string, any>) => ({
  ...ucdContentGenerationMeta,
  ucdContentGenerationResponse: true,
  ucdContentGenerationChannel: ucdPayload && ucdPayload.input && ucdPayload.input.content_type,
});

// Attribute failures are represented in the generated-content modal. Dispatch
// the normal failure action without invoking the middleware's global snackbar
// for every failed attribute in the batch.
const quietContentConfigGenerationError = (error, next, action) => next({
  ...action,
  type: action.type + FAILURE,
  payload: error,
  meta: {
    ...action.meta,
    request: action.payload,
    requestStatus: FAILURE,
    requestTime: new Date(),
  },
});

export const contentGenerationGetCountriesConfig = () => ({
  type: CONTENT_GENERATION_GET_COUNTRIES_CONFIG,
  payload: {
    method: 'GET',
    url: MYSTIQUE_COUNTRIES_CONFIG_PATH,
  },
  meta: mystiqueContentGenerationMeta,
});

export const contentGenerationListProductOfferings = (country: string, vertical: string) => ({
  type: CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS,
  payload: {
    method: 'GET',
    url: MYSTIQUE_PRODUCT_OFFERINGS_PATH,
    params: {
      country,
      vertical,
    },
  },
  meta: mystiqueContentGenerationMeta,
});

export const contentGenerationListAttributes = (
  country: string,
  vertical: string,
  productOffering: string,
) => ({
  type: CONTENT_GENERATION_LIST_ATTRIBUTES,
  payload: {
    method: 'GET',
    url: MYSTIQUE_ATTRIBUTES_PATH,
    params: {
      country,
      vertical,
      productOffering,
    },
  },
  meta: mystiqueContentGenerationMeta,
});

export const contentGenerationGenerateMystiqueV1 = (payload) => ({
  type: CONTENT_GENERATION_GENERATE_MYSTIQUE_V1,
  payload: {
    method: 'POST',
    url: MYSTIQUE_GENERATE_CONTENT_V1_PATH,
    data: payload,
  },
  meta: mystiqueContentGenerationMeta,
});

const contentConfigGenerateMystiqueV2Attribute = (
  request: {
    audienceKey?: string;
    attribute?: string;
    payload: Record<string, any>;
    ucdPayload?: Record<string, any>;
  },
) => {
  const shouldUseUcdContentGeneration = shouldUseUcdContentGenerationPayload(request.ucdPayload);

  return {
    type: CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE,
    payload: {
      method: 'POST',
      url: shouldUseUcdContentGeneration
        ? UCD_CONTENT_GENERATION_TEST_GENERATE_PATH
        : MYSTIQUE_GENERATE_CONTENT_V2_PATH,
      data: shouldUseUcdContentGeneration ? request.ucdPayload : request.payload,
    },
    meta: {
      ...(shouldUseUcdContentGeneration ? ucdContentGenerationMeta : mystiqueContentGenerationMeta),
      attribute: request.audienceKey || request.attribute || '',
      errorHandler: quietContentConfigGenerationError,
    },
  };
};

const contentConfigGenerationQueueAction = (
  requestStatus,
  requestKeys,
  preserveExisting = false,
) => ({
  type: CONTENT_CONFIG_GENERATE_MYSTIQUE_V2 + requestStatus,
  meta: {
    attributes: requestKeys,
    preserveExisting,
    requestStatus,
    requestTime: new Date(),
  },
});

export const contentConfigGenerateMystiqueV2 = (
  requests: {
    audienceKey?: string;
    attribute?: string;
    payload: Record<string, any>;
    ucdPayload?: Record<string, any>;
  }[],
  { preserveExisting = false }: { preserveExisting?: boolean } = {},
) => async (dispatch) => {
  const requestKeys = requests.map((request) => request.audienceKey || request.attribute || '');
  dispatch(contentConfigGenerationQueueAction(REQUEST, requestKeys, preserveExisting));

  for (let index = 0; index < requests.length; index += CONTENT_CONFIG_GENERATION_CONCURRENCY) {
    const batch = requests.slice(index, index + CONTENT_CONFIG_GENERATION_CONCURRENCY);
    // Run at most five requests together, then start the next batch.
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.map((request) =>
      dispatch(contentConfigGenerateMystiqueV2Attribute(request))));
  }

  // Attribute failures are tracked by the UCD reducer and surfaced by the
  // modal. Complete the queue request successfully so a synthetic FAILURE
  // action does not trigger the API middleware's generic error snackbar.
  dispatch(contentConfigGenerationQueueAction(SUCCESS, requestKeys));
};

export const generateContentViaMystique = (params) => {
  const {
    language,
    audience,
    product,
    marketing,
    tone,
    reason_to_use,
    content_type,
    call_to_action,
    key_pointers,
    num,
    personal_attributes,
    country,
    ucdPayload,
  } = params;
  const productName = params.product_name;
  // Check if Mystique API v3 is enabled
  const isApiV3Enabled = (window.Grab && window.Grab.config && window.Grab.config.ENABLE_MYSTIQUE_API_V3) || false;
  const apiUrl = isApiV3Enabled ? '/generate_text_lc3/' : '/generate_text_lc2/';

  // Base data payload
  const baseData = {
    grab_gxs: SMART_CAMPAIGN_MYSTIQUE_GRAB_GXS,
    language,
    pax_mex_dax: audience,
    num: num || SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
    content_type,
    product,
    marketing,
    reason_to_use,
    use_emoji: true,
    crm: SMART_CAMPAIGN_MYSTIQUE_CRM,
    call_to_action,
    key_pointers: key_pointers || '',
    l2_cta: '',
    l1_persona: SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA,
    l2_personas: '',
    tone,
    // NOTE: This is done because, if we pass empty array to mystique, then personal attribute is getting appended to title/message in mystique response.
    ...(personal_attributes &&
      personal_attributes.length && { personal_attributes }),
  };

  // Add new fields for API v3
  if (isApiV3Enabled) {
    if (country) {
      baseData.country = country;
    }
    if (productName) {
      baseData.product_name = productName;
    }
  }

  const shouldUseUcdContentGeneration = shouldUseUcdContentGenerationPayload(ucdPayload);

  return {
    type: UCD_GENERATE_MYSTIQUE_CONTENT,
    payload: {
      method: 'POST',
      url: shouldUseUcdContentGeneration ? UCD_CONTENT_GENERATION_TEST_GENERATE_PATH : apiUrl,
      data: shouldUseUcdContentGeneration ? ucdPayload : baseData,
    },
    meta: shouldUseUcdContentGeneration
      ? ucdContentGenerationResponseMeta(ucdPayload)
      : mystiqueContentGenerationMeta,
  };
};

export const CLEAR_MYSTIQUE_CONTENT = 'CLEAR_MYSTIQUE_CONTENT';

export const clearMystiqueContent = () => {
  return {
    type: CLEAR_MYSTIQUE_CONTENT,
  };
};

export const UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK =
  'UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK';

export const generateContentViaMystiqueFeedback = ({
  language,
  audience,
  product,
  marketing,
  tone,
  reason_to_use,
  content_type,
  call_to_action,
  output,
  feedback,
  key_pointers,
  num,
  personal_attributes,
  ucdPayload,
}) => {
  const shouldUseUcdContentGeneration = shouldUseUcdContentGenerationPayload(ucdPayload);
  const baseData = {
    grab_gxs: SMART_CAMPAIGN_MYSTIQUE_GRAB_GXS,
    language,
    pax_mex_dax: audience,
    num: num || SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
    content_type,
    product,
    marketing,
    reason_to_use,
    use_emoji: true,
    crm: SMART_CAMPAIGN_MYSTIQUE_CRM,
    call_to_action,
    key_pointers: key_pointers || '',
    l2_cta: '',
    l1_persona: SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA,
    l2_personas: '',
    tone,
    output,
    feedback,
    // NOTE: This is done because, if we pass empty array to mystique, then personal attribute is getting appended to title/message in mystique response.
    ...(personal_attributes &&
      personal_attributes.length && { personal_attributes }),
  };

  return {
    type: UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK,
    payload: {
      method: 'POST',
      url: shouldUseUcdContentGeneration
        ? UCD_CONTENT_GENERATION_TEST_GENERATE_PATH
        : '/generate_text_lc_feedback/',
      data: shouldUseUcdContentGeneration ? ucdPayload : baseData,
    },
    meta: shouldUseUcdContentGeneration
      ? ucdContentGenerationResponseMeta(ucdPayload)
      : mystiqueContentGenerationMeta,
  };
};

export const UPLOAD_IMAGE_TO_HEDWIG = 'UPLOAD_IMAGE_TO_HEDWIG';

export const uploadImageToHegwig = ({
  size,
  fileName,
  extension,
  base64Str,
  contentType,
  path = 'smartcomms',
}) => {
  return {
    type: UPLOAD_IMAGE_TO_HEDWIG,
    payload: {
      method: 'POST',
      url: '/v1/upload/image',
      data: {
        path,
        file: { fileName, contentType, extension, base64Str, size },
      },
    },
    meta: {
      [API_REQUEST]: true,
      [SERVICE_NAME]: SERVICE_NAMES.HEDWIG,
    },
  };
};

export const CLEAR_IMAGE = 'CLEAR_IMAGE';

export const clearImage = () => {
  return {
    type: CLEAR_IMAGE,
  };
};

export const SEND_PREVIEW = 'SEND_PREVIEW';

export const sendPreview = ({
  recipientID,
  language,
  templateParams,
  requestID = '', // used to keep track of delivered and undelivered messages
  channel = SMART_CAMPAIGN_CHANNELS.push,
}) => {
  const hedwigTemplateMap = {
    [SMART_CAMPAIGN_CHANNELS.push]:
      window.Grab.config.PUSH_PREVIEW_HEDWIG_TEMPLATE_ID,
    [SMART_CAMPAIGN_CHANNELS.pci]:
      window.Grab.config.PCI_PREVIEW_HEDWIG_TEMPLATE_ID,
    default: window.Grab.config.PUSH_PREVIEW_HEDWIG_TEMPLATE_ID,
  };
  const id = hedwigTemplateMap[channel] || hedwigTemplateMap.default;

  return {
    type: SEND_PREVIEW,
    payload: {
      method: 'POST',
      url: '/v1/preview-message',
      data: {
        recipientID,
        recipientType: 'passenger', // This hard coding to be removed once we have multiple recipient types
        template: { id, language, params: templateParams },
      },
    },
    meta: {
      requestID,
      [API_REQUEST]: true,
      [SERVICE_NAME]: SERVICE_NAMES.HEDWIG,
    },
  };
};

export const CLEAR_PREVIEW_MESSAGE_STORE = 'CLEAR_PREVIEW_MESSAGE';

export const clearPreviewMessageStore = () => {
  return { type: CLEAR_PREVIEW_MESSAGE_STORE };
};

// ---- Content Config Portal (ucd content portal endpoints) ----

const cpMeta = { [API_REQUEST]: true };
const CP_STATUS_PENDING = 'CONTENT_CONFIG_STATUS_PENDING_APPROVAL';
const cpStatusKey = (status) => (status === CP_STATUS_PENDING ? 'pending' : 'active');

export const CP_GET_VERTICALS = 'CP_GET_VERTICALS';
export const cpGetVerticals = (): FSA => ({
  type: CP_GET_VERTICALS,
  payload: { method: 'GET', url: '/ucd/verticals' },
  meta: cpMeta,
});

export const CP_GET_COUNTRIES = 'CP_GET_COUNTRIES';
export const cpGetCountries = (): FSA => ({
  type: CP_GET_COUNTRIES,
  payload: { method: 'GET', url: '/ucd/countries' },
  meta: cpMeta,
});

export const CP_GET_PERSONAS = 'CP_GET_PERSONAS';
export const cpGetPersonas = (): FSA => ({
  type: CP_GET_PERSONAS,
  payload: { method: 'GET', url: '/ucd/personas' },
  meta: cpMeta,
});

export const CP_LIST_ATTRIBUTES = 'CP_LIST_ATTRIBUTES';
export const cpListAttributes = (): FSA => ({
  type: CP_LIST_ATTRIBUTES,
  payload: { method: 'GET', url: '/ucd/attributes' },
  meta: cpMeta,
});

export const CP_LIST_CVPS = 'CP_LIST_CVPS';
export const CP_LIST_CVPS_PENDING = 'CP_LIST_CVPS_PENDING';
export const cpListCvps = (country: string, status: string = ''): FSA => ({
  // Active and pending lists are fetched in parallel. Tag the pending fetch
  // with its own action type so it gets a separate request flag
  // (cpListCvpsPendingRequest) — otherwise both share cpListCvpsRequest and the
  // first response clears isPending while the other is still in flight.
  type: status === CP_STATUS_PENDING ? CP_LIST_CVPS_PENDING : CP_LIST_CVPS,
  payload: {
    method: 'POST',
    url: '/ucd/cvps/list',
    data: { filter: { country, ...(status ? { status } : {}) } },
  },
  meta: { ...cpMeta, country, statusKey: cpStatusKey(status) },
});

export const CP_UPDATE_CVP = 'CP_UPDATE_CVP';
export const cpUpdateCvp = (id: string, description: string, user: string): FSA => ({
  type: CP_UPDATE_CVP,
  payload: { method: 'PUT', url: `/ucd/cvps/${id}`, data: { id, body: { description }, user } },
  meta: cpMeta,
});

export const CP_LIST_PRODUCTS = 'CP_LIST_PRODUCTS';
export const CP_LIST_PRODUCTS_PENDING = 'CP_LIST_PRODUCTS_PENDING';
export const cpListProducts = (country: string, status: string = ''): FSA => ({
  // Pending fetch gets its own action type (and request flag,
  // cpListProductsPendingRequest) so it does not collide with the parallel
  // active fetch sharing cpListProductsRequest.
  type: status === CP_STATUS_PENDING ? CP_LIST_PRODUCTS_PENDING : CP_LIST_PRODUCTS,
  payload: {
    method: 'POST',
    url: '/ucd/products/list',
    data: { filter: { country, ...(status ? { status } : {}) } },
  },
  // statusKey lets the reducer keep ACTIVE and PENDING lists side by side.
  meta: { ...cpMeta, country, statusKey: cpStatusKey(status) },
});

export const CP_GET_PRODUCT = 'CP_GET_PRODUCT';
export const cpGetProduct = (country: string, key: string): FSA => ({
  type: CP_GET_PRODUCT,
  payload: { method: 'GET', url: `/ucd/products/${country}/${key}` },
  meta: cpMeta,
});

export const CP_CREATE_PRODUCT = 'CP_CREATE_PRODUCT';
export const cpCreateProduct = (country: string, body: Object, user: string): FSA => ({
  type: CP_CREATE_PRODUCT,
  payload: { method: 'POST', url: '/ucd/products', data: { country, body, user } },
  meta: cpMeta,
});

export const CP_UPDATE_PRODUCT = 'CP_UPDATE_PRODUCT';
export const cpUpdateProduct = (id: string, body: Object, user: string): FSA => ({
  type: CP_UPDATE_PRODUCT,
  payload: { method: 'PUT', url: `/ucd/products/${id}`, data: { id, body, user } },
  meta: cpMeta,
});

export const CP_LIST_AUDIENCES = 'CP_LIST_AUDIENCES';
export const CP_LIST_AUDIENCES_PENDING = 'CP_LIST_AUDIENCES_PENDING';
export const CP_AUDIENCE_ALL_SCOPE_KEY = '__all__';
export const CP_AUDIENCE_REGIONAL_SCOPE_KEY = '__regional__';
export const cpListAudiences = (
  country?: string,
  status: string = ContentConfigStatus.Active,
): FSA => {
  const hasCountryFilter = country !== undefined;
  const scopeKey = !hasCountryFilter
    ? CP_AUDIENCE_ALL_SCOPE_KEY
    : country || CP_AUDIENCE_REGIONAL_SCOPE_KEY;
  return {
    type: status === CP_STATUS_PENDING ? CP_LIST_AUDIENCES_PENDING : CP_LIST_AUDIENCES,
    payload: {
      method: 'POST',
      url: UCD_AUDIENCES_LIST_PATH,
      data: {
        filter: {
          ...(hasCountryFilter ? { country } : {}),
          status,
          page_size: 0,
        },
      },
    },
    meta: {
      ...cpMeta,
      country,
      scopeKey,
      statusKey: cpStatusKey(status),
    },
  };
};

// cpGetAudience resolves a single Audience by its stable key. UCD returns at
// most one ACTIVE plus one PENDING_APPROVAL row for (country, key), and widens
// the lookup to regional audiences (country == '') on its side — so `country`
// must be the campaign's country code. An empty country matches regional
// audiences only.
export const CP_GET_AUDIENCE = 'CP_GET_AUDIENCE';
// cpAudienceStateKey keys the cpAudienceByKey slice. Shared so the reducer and
// its consumers cannot drift apart.
export const cpAudienceStateKey = (country: string, key: string) => `${country || ''}:${key}`;
export const cpGetAudience = (country: string, key: string): FSA => ({
  type: CP_GET_AUDIENCE,
  payload: {
    method: 'GET',
    url: `${UCD_AUDIENCES_PATH}/${key}`,
    params: { country },
  },
  meta: { ...cpMeta, country, key },
});

export const CP_GET_AUDIENCE_TABLE_INFO = 'CP_GET_AUDIENCE_TABLE_INFO';
export const cpGetAudienceTableInfo = (table: string): FSA => ({
  type: CP_GET_AUDIENCE_TABLE_INFO,
  payload: {
    method: 'POST',
    url: UCD_AUDIENCES_INFO_PATH,
    data: { table },
  },
  meta: { ...cpMeta, table },
});

export const CP_CREATE_AUDIENCE = 'CP_CREATE_AUDIENCE';
export const cpCreateAudience = (
  country: string,
  body: AudienceBodyApi,
  user: string,
): FSA => ({
  type: CP_CREATE_AUDIENCE,
  payload: {
    method: 'POST',
    url: UCD_AUDIENCES_PATH,
    data: { country, body, user },
  },
  meta: cpMeta,
});

export const CP_UPDATE_AUDIENCE = 'CP_UPDATE_AUDIENCE';
export const cpUpdateAudience = (
  id: string,
  body: AudienceBodyApi,
  user: string,
): FSA => ({
  type: CP_UPDATE_AUDIENCE,
  payload: {
    method: 'PUT',
    url: `${UCD_AUDIENCES_PATH}/${id}`,
    data: { id, body, user },
  },
  meta: cpMeta,
});

// ---- Events (Seasonality Phase 1) ----
// Events are always country-scoped; an empty `country` filter lists every
// country. Like CVPs and Products, the PENDING fetch runs in parallel with the
// ACTIVE one and needs its own action type so the two do not share
// cpListEventsRequest — otherwise the first response clears isPending while
// the other is still in flight.
export const CP_LIST_EVENTS = 'CP_LIST_EVENTS';
export const CP_LIST_EVENTS_PENDING = 'CP_LIST_EVENTS_PENDING';
export const cpListEvents = (country: string, status: string = ''): FSA => ({
  type: status === CP_STATUS_PENDING ? CP_LIST_EVENTS_PENDING : CP_LIST_EVENTS,
  payload: {
    method: 'POST',
    url: UCD_EVENTS_LIST_PATH,
    data: {
      filter: {
        country,
        ...(status ? { status } : {}),
        page_size: 0,
      },
    },
  },
  meta: { ...cpMeta, country, statusKey: cpStatusKey(status) },
});

export const CP_CREATE_EVENT = 'CP_CREATE_EVENT';
export const cpCreateEvent = (
  country: string,
  body: EventBodyApi,
  user: string,
): FSA => ({
  type: CP_CREATE_EVENT,
  payload: {
    method: 'POST',
    url: UCD_EVENTS_PATH,
    data: { country, body, user },
  },
  meta: cpMeta,
});

export const CP_UPDATE_EVENT = 'CP_UPDATE_EVENT';
export const cpUpdateEvent = (
  id: string,
  body: EventBodyApi,
  user: string,
): FSA => ({
  type: CP_UPDATE_EVENT,
  payload: {
    method: 'PUT',
    url: `${UCD_EVENTS_PATH}/${id}`,
    data: { id, body, user },
  },
  meta: cpMeta,
});

export const CP_APPROVE = 'CP_APPROVE';
export const cpApprove = (pendingId: string, user: string, comment: string = ''): FSA => ({
  type: CP_APPROVE,
  payload: { method: 'POST', url: '/ucd/approvals/approve', data: { pending_id: pendingId, user, comment } },
  meta: cpMeta,
});

export const CP_REJECT = 'CP_REJECT';
export const cpReject = (pendingId: string, user: string, comment: string): FSA => ({
  type: CP_REJECT,
  payload: { method: 'POST', url: '/ucd/approvals/reject', data: { pending_id: pendingId, user, comment } },
  meta: cpMeta,
});

export const CP_RECALL = 'CP_RECALL';
export const cpRecall = (pendingId: string, user: string): FSA => ({
  type: CP_RECALL,
  payload: { method: 'POST', url: '/ucd/approvals/recall', data: { pending_id: pendingId, user } },
  meta: cpMeta,
});

export const CONTENT_GENERATION_CONFIG_KEY = 'content-generation:prompt-pack';
export const CONTENT_GENERATION_CONFIG_FILE_CHANGE_ACTION_UPSERT =
  'CONTENT_GENERATION_CONFIG_FILE_CHANGE_ACTION_UPSERT';
export const CONTENT_GENERATION_CONFIG_VERSION_STATUS_ACTIVE =
  'CONTENT_GENERATION_CONFIG_VERSION_STATUS_ACTIVE';
export const CONTENT_GENERATION_CONFIG_VERSION_STATUS_DRAFT =
  'CONTENT_GENERATION_CONFIG_VERSION_STATUS_DRAFT';

export const CONTENT_GENERATION_CONFIG_GET_LATEST = 'CONTENT_GENERATION_CONFIG_GET_LATEST';
export const contentGenerationConfigGetLatest = (
  configKey: string = CONTENT_GENERATION_CONFIG_KEY,
): FSA => ({
  type: CONTENT_GENERATION_CONFIG_GET_LATEST,
  payload: {
    method: 'GET',
    url: UCD_CONTENT_GENERATION_CONFIG_LATEST_PATH,
    params: { config_key: configKey },
  },
  meta: {
    ...cpMeta,
    [CHECK_NETWORK]: true,
  },
});

export const CONTENT_GENERATION_CONFIG_LIST_VERSIONS =
  'CONTENT_GENERATION_CONFIG_LIST_VERSIONS';
export const contentGenerationConfigListVersions = (
  configKey: string = CONTENT_GENERATION_CONFIG_KEY,
  pageSize: number = 20,
  pageToken: string = '',
): FSA => ({
  type: CONTENT_GENERATION_CONFIG_LIST_VERSIONS,
  payload: {
    method: 'GET',
    url: '/ucd/content-generation/config/versions',
    params: {
      config_key: configKey,
      page_size: pageSize,
      ...(pageToken ? { page_token: pageToken } : {}),
    },
  },
  meta: {
    ...cpMeta,
    pageToken,
  },
});

export const CONTENT_GENERATION_CONFIG_READ_FILE = 'CONTENT_GENERATION_CONFIG_READ_FILE';
export const contentGenerationConfigReadFile = (
  configKey: string,
  version: number,
  path: string,
): FSA => ({
  type: CONTENT_GENERATION_CONFIG_READ_FILE,
  payload: {
    method: 'POST',
    url: '/ucd/content-generation/config/files/read',
    data: { config_key: configKey, version, path },
  },
  meta: {
    ...cpMeta,
    version,
    path,
  },
});

export const CONTENT_GENERATION_CONFIG_APPLY = 'CONTENT_GENERATION_CONFIG_APPLY';
export const contentGenerationConfigApply = ({
  configKey = CONTENT_GENERATION_CONFIG_KEY,
  expectedBaseVersion,
  path,
  contentType,
  content,
  user,
  comment,
  status = CONTENT_GENERATION_CONFIG_VERSION_STATUS_ACTIVE,
  name = '',
}: {
  configKey?: string,
  expectedBaseVersion: number,
  path: string,
  contentType: string,
  content: string,
  user: string,
  comment?: string,
  status?: string,
  name?: string,
}): FSA => ({
  type: CONTENT_GENERATION_CONFIG_APPLY,
  payload: {
    method: 'POST',
    url: '/ucd/content-generation/config/apply',
    data: {
      config_key: configKey,
      expected_base_version: expectedBaseVersion,
      file_changes: [{
        action: CONTENT_GENERATION_CONFIG_FILE_CHANGE_ACTION_UPSERT,
        path,
        content_type: contentType,
        content,
      }],
      user,
      comment: comment || '',
      status,
      name,
    },
  },
  meta: cpMeta,
});

export const CONTENT_GENERATION_CONFIG_ROLLBACK = 'CONTENT_GENERATION_CONFIG_ROLLBACK';
export const contentGenerationConfigRollback = ({
  configKey = CONTENT_GENERATION_CONFIG_KEY,
  targetVersion,
  expectedBaseVersion,
  user,
  comment,
}: {
  configKey?: string,
  targetVersion: number,
  expectedBaseVersion: number,
  user: string,
  comment?: string,
}): FSA => ({
  type: CONTENT_GENERATION_CONFIG_ROLLBACK,
  payload: {
    method: 'POST',
    url: '/ucd/content-generation/config/rollback',
    data: {
      config_key: configKey,
      target_version: targetVersion,
      expected_base_version: expectedBaseVersion,
      user,
      comment: comment || '',
    },
  },
  meta: cpMeta,
});
